import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { CompanyForm } from "@/components/settings/CompanyForm";
import { GroupForm } from "@/components/settings/GroupForm";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/form";
import { Spinner } from "@/components/ui/card";
import { ApiError, api, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { GroupName } from "@/lib/settingsFields";

const STORAGE_KEY = "ep.setup-token";
const STEPS = [
  "company",
  "owner",
  "storage",
  "email",
  "sms",
  "hosting",
  "backup",
  "finish",
] as const;
type Step = (typeof STEPS)[number];
const STEP_LABEL = {
  company: "setup.step_company",
  owner: "setup.step_owner",
  storage: "setup.step_storage",
  email: "setup.step_email",
  sms: "setup.step_sms",
  hosting: "setup.step_hosting",
  backup: "setup.step_backup",
  finish: "setup.step_finish",
} as const;
const OPTIONAL: Step[] = ["storage", "email", "sms", "hosting", "backup"];
const APPLY_ERRORS = {
  no_agent: "settings.apply_no_agent",
  incomplete: "settings.apply_incomplete",
} as const;

function readToken(fromUrl: string | null): string {
  try {
    if (fromUrl) sessionStorage.setItem(STORAGE_KEY, fromUrl);
    return fromUrl ?? sessionStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return fromUrl ?? "";
  }
}

function OwnerStep({
  token,
  exists,
  onDone,
}: {
  token: string;
  exists: boolean;
  onDone: () => void;
}) {
  const { t } = useT();
  const [v, setV] = useState({ phone: "", full_name: "", password: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: async () => {
      const res = await api.POST("/api/setup/owner/", {
        body: v,
        headers: { "X-Setup-Token": token },
      });
      if (!res.response.ok) throw new ApiError(res.response.status, res.error);
    },
    onSuccess: onDone,
    onError: (e) => {
      const body =
        e instanceof ApiError
          ? (e.body as { errors?: Record<string, string>; detail?: string })
          : undefined;
      setErrors(body?.errors ?? { _: body?.detail ?? t("common.error") });
    },
  });
  if (exists) return <p className="text-sm text-success">{t("setup.owner_exists")}</p>;
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setErrors({});
        create.mutate();
      }}
    >
      <Field label={t("setup.owner_name")}>
        {(id) => (
          <Input
            id={id}
            value={v.full_name}
            onChange={(e) => setV({ ...v, full_name: e.target.value })}
            required
          />
        )}
      </Field>
      <Field label={t("setup.owner_phone")} error={errors.phone ?? null}>
        {(id) => (
          <Input
            id={id}
            inputMode="tel"
            placeholder="+998 90 123 45 67"
            value={v.phone}
            onChange={(e) => setV({ ...v, phone: e.target.value })}
            required
          />
        )}
      </Field>
      <Field label={t("setup.owner_password")} error={errors.password ?? null}>
        {(id) => (
          <Input
            id={id}
            type="password"
            autoComplete="new-password"
            value={v.password}
            onChange={(e) => setV({ ...v, password: e.target.value })}
            required
            minLength={8}
          />
        )}
      </Field>
      {errors._ && (
        <p role="alert" className="text-sm text-danger">
          {errors._}
        </p>
      )}
      <div>
        <Button
          type="submit"
          disabled={create.isPending || !v.full_name || !v.phone || v.password.length < 8}
        >
          {t("setup.owner_create")}
        </Button>
      </div>
    </form>
  );
}

/** Birinchi ishga tushirish ustasi. Login'siz; bir martalik token (havoladan) har so'rovda X-Setup-Token sarlavhasida. */
export function SetupPage() {
  const { t, lang, setLang } = useT();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [token] = useState(() => readToken(params.get("token")));
  const [step, setStep] = useState<Step>("company");
  const [done, setDone] = useState(false);
  const headers = { "X-Setup-Token": token };

  const status = useQuery({
    queryKey: ["setup", "status"],
    queryFn: async () => unwrap(await api.GET("/api/setup/status/")),
  });
  const state = useQuery({
    queryKey: ["setup", "state", token],
    enabled: !!token && status.data?.completed === false,
    retry: false,
    queryFn: async () => unwrap(await api.GET("/api/setup/state/", { headers })),
  });
  const reload = () => void qc.invalidateQueries({ queryKey: ["setup", "state"] });

  const complete = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/setup/complete/", { headers })),
    onSuccess: () => setDone(true),
  });

  const shell = (children: React.ReactNode) => (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <Zap className="size-6 text-primary" /> {t("setup.title")}
        </h1>
        <div className="flex items-center gap-1" role="group" aria-label="language">
          <ThemeToggle />
          {(["uz", "ru"] as const).map((l) => (
            <Button
              key={l}
              size="sm"
              variant={lang === l ? "primary" : "outline"}
              onClick={() => setLang(l)}
            >
              {l.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
      {children}
    </div>
  );

  if (status.isLoading) return shell(<Spinner className="mx-auto my-10 size-8" />);
  if (status.data?.completed || done) {
    return shell(
      <Card>
        <p className="mb-4">{t("setup.completed")}</p>
        <Link
          to="/login"
          className="inline-flex min-h-11 items-center rounded-lg bg-primary px-4 font-semibold text-primary-foreground"
        >
          {t("setup.go_login")}
        </Link>
      </Card>,
    );
  }
  if (!token)
    return shell(
      <p role="alert" className="text-danger">
        {t("setup.no_token")}
      </p>,
    );
  if (state.isError) {
    const code =
      state.error instanceof ApiError && state.error.body && typeof state.error.body === "object"
        ? (state.error.body as { code?: string }).code
        : undefined;
    return shell(
      <p role="alert" className="text-danger">
        {code === "encryption" ? t("settings.encryption_error") : t("setup.bad_token")}
      </p>,
    );
  }
  if (!state.data) return shell(<Spinner className="mx-auto my-10 size-8" />);

  const s = state.data;
  const idx = STEPS.indexOf(step);
  const company = s.company;
  const canNext = step === "company" ? !!company : step === "owner" ? s.owner_exists : true;
  // Server bu guruhni qaytarmasa sahifa yiqilmasin (eski/yangi versiya aralashsa)
  const group = (g: GroupName) => s.groups?.[g];
  const isSet = (g: GroupName, key: string) =>
    group(g)?.settings?.find((x) => x.key === key)?.set ?? false;
  const summary: [Step, boolean][] = [
    ["company", !!company],
    ["owner", s.owner_exists],
    ["storage", isSet("storage", "storage.access_key")],
    ["email", isSet("email", "email.host")],
    ["sms", isSet("sms", "sms.eskiz_email") || isSet("sms", "sms.playmobile_login")],
    ["hosting", isSet("hosting", "hosting.hetzner_token")],
    ["backup", isSet("backup", "backup.repository")],
  ];

  const groupStep = (g: GroupName) =>
    !group(g) ? (
      <p role="alert" className="text-danger">
        {t("common.error")}
      </p>
    ) : (
      <GroupForm
        group={g}
        data={group(g)!}
        onSave={async (values) => {
          unwrap(await api.PATCH("/api/setup/settings/", { body: { values }, headers }));
          reload();
        }}
        onVerify={async (values) => {
          const r = unwrap(
            await api.POST("/api/setup/verify/{group}/", {
              params: { path: { group: g } },
              body: { values },
              headers,
            }),
          );
          reload();
          return r;
        }}
        extra={g === "backup" ? <SetupBackupApply token={token} /> : undefined}
      />
    );

  return shell(
    <>
      <nav aria-label="steps" className="mb-4 flex flex-wrap gap-1.5 text-xs">
        {STEPS.map((x, i) => (
          <span
            key={x}
            aria-current={x === step ? "step" : undefined}
            className={`rounded-full px-2.5 py-1 ${x === step ? "bg-primary text-primary-foreground" : i < idx ? "bg-muted" : "border border-border"}`}
          >
            {t(STEP_LABEL[x])}
          </span>
        ))}
      </nav>
      <Card>
        <p className="mb-3 text-sm text-muted-foreground">
          {t("setup.progress", { n: String(idx + 1), total: String(STEPS.length) })}
        </p>
        <h2 className="mb-3 text-lg font-bold">{t(STEP_LABEL[step])}</h2>
        {step === "company" && (
          <CompanyForm
            key={company?.id ?? "new"}
            company={company}
            saveLabel={t("settings.save")}
            onSave={async (v) => {
              unwrap(await api.PUT("/api/setup/company/", { body: v, headers }));
              reload();
            }}
            onUpload={async (file) => {
              const form = new FormData();
              form.append("file", file);
              unwrap(
                await api.POST("/api/setup/company/logo/", {
                  body: form as never,
                  bodySerializer: (b: unknown) => b as FormData,
                  headers,
                }),
              );
              reload();
            }}
          />
        )}
        {step === "owner" && <OwnerStep token={token} exists={s.owner_exists} onDone={reload} />}
        {(step === "storage" ||
          step === "email" ||
          step === "sms" ||
          step === "hosting" ||
          step === "backup") &&
          groupStep(step)}
        {step === "finish" && (
          <div className="flex flex-col gap-3">
            <h3 className="font-bold">{t("setup.finish_title")}</h3>
            <ul className="space-y-1 text-sm" data-testid="summary">
              {summary.map(([k, ok]) => (
                <li key={k} className="flex justify-between gap-3">
                  <span>{t(STEP_LABEL[k])}</span>
                  <span className={ok ? "text-success" : "text-muted-foreground"}>
                    {ok ? t("setup.summary_done") : t("setup.summary_skipped")}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">{t("setup.finish_note")}</p>
            {complete.isError && (
              <p role="alert" className="text-sm text-danger">
                {complete.error instanceof ApiError && complete.error.status === 400
                  ? t("setup.finish_missing")
                  : t("common.error") +
                    (complete.error instanceof ApiError ? ` (${complete.error.status})` : "")}
              </p>
            )}
            <div>
              <Button
                onClick={() => complete.mutate()}
                disabled={complete.isPending || !company || !s.owner_exists}
              >
                {t("setup.finish_btn")}
              </Button>
            </div>
          </div>
        )}
      </Card>
      <div className="mt-4 flex justify-between">
        <Button variant="outline" onClick={() => setStep(STEPS[idx - 1]!)} disabled={idx === 0}>
          {t("setup.back")}
        </Button>
        {step !== "finish" && (
          <div className="flex gap-2">
            {OPTIONAL.includes(step) && (
              <Button variant="ghost" onClick={() => setStep(STEPS[idx + 1]!)}>
                {t("setup.skip")}
              </Button>
            )}
            <Button onClick={() => setStep(STEPS[idx + 1]!)} disabled={!canNext}>
              {t("setup.next")}
            </Button>
          </div>
        )}
      </div>
    </>,
  );
}

function SetupBackupApply({ token }: { token: string }) {
  const { t } = useT();
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const apply = useMutation({
    mutationFn: async () => {
      const res = await api.POST("/api/setup/backup/apply/", {
        headers: { "X-Setup-Token": token },
      });
      if (!res.response.ok) throw new ApiError(res.response.status, res.error);
    },
    onSuccess: () => setMsg({ ok: true, text: t("settings.apply_pending") }),
    onError: (e) => {
      const code =
        e instanceof ApiError && e.body && typeof e.body === "object"
          ? (e.body as { code?: string }).code
          : undefined;
      setMsg({
        ok: false,
        text:
          code && code in APPLY_ERRORS
            ? t(APPLY_ERRORS[code as keyof typeof APPLY_ERRORS])
            : t("common.error"),
      });
    },
  });
  return (
    <div className="rounded-lg bg-muted p-3">
      <p className="mb-2 text-sm text-muted-foreground">{t("settings.apply_hint")}</p>
      <Button
        type="button"
        variant="outline"
        onClick={() => apply.mutate()}
        disabled={apply.isPending}
      >
        {t("settings.apply")}
      </Button>
      {msg && (
        <p
          role={msg.ok ? "status" : "alert"}
          className={`mt-2 text-sm ${msg.ok ? "text-muted-foreground" : "text-danger"}`}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
}
